'use client';

// Re-exported for existing importers. The values themselves MUST live in a
// non-client module: a Server Component importing them from here would get a
// client reference and render the class as empty, which is exactly how every
// free-tool primary CTA silently lost its gradient once.
export { BRAND_GRADIENT, GRADIENT_CTA_CLASS } from '@/components/free-tools/brand';

// `export … from` re-exports without binding locally, so this component needs
// its own import to use the class.
import { GRADIENT_CTA_CLASS } from '@/components/free-tools/brand';

import Link from 'next/link';
import { useMemo } from 'react';

import type { FreeToolKind } from '@/lib/free-tools/types';
import { buildTranscribeHandoffPath } from '@/lib/free-tools/transcribe-handoff';
import { pushToDataLayer } from '@/lib/analytics/gtm';
import { emitClientEvent } from '@/lib/analytics/emit-client-event';
import {
  SIGNUP_ATTR_COOKIE,
  SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS,
  buildAttributionFromRequest,
  decodeAttrCookie,
} from '@/lib/analytics/signup-source-config';
import { encodeAttrCookie, type SignupSource } from '@/lib/analytics/signup-source-config';
import { cn } from '@/lib/utils';

export type TranscribeCtaToolKind = FreeToolKind | 'free-audio-to-text';

/** Upgrade the wisprs_attr cookie to include the jobId once we have one — preserves whatever
 *  attribution was captured on landing. The cookie's write-once semantics live on the server
 *  (stampSignupSourceOnce); on the client we always refresh so the jobId wins.
 *
 *  If no cookie exists yet, both AttributionCapture's mount-time write AND this one have missed —
 *  same middleware-skip reasoning as attribution-capture.tsx. Classify the touch here too, rather
 *  than falling back to a channel-less `{ tool, capturedAt }` object that reads as "—" forever. */
function stampJobIdOnAttrCookie(freeToolKind: TranscribeCtaToolKind, freeToolJobId: string): void {
  if (typeof document === 'undefined') return;

  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith(`${SIGNUP_ATTR_COOKIE}=`))
    ?.split('=')[1];

  const decoded = decodeAttrCookie(raw);
  const capturedAt = decoded?.capturedAt ?? new Date().toISOString();

  const existing: SignupSource = decoded ?? {
    ...(buildAttributionFromRequest({
      searchParams: new URL(window.location.href).searchParams,
      referer: document.referrer || null,
      selfHost: window.location.host,
      landing: window.location.pathname,
      capturedAt,
    }) ?? {
      channel: 'direct',
      attributionScope: 'first-touch',
      sourceEvidence: 'no-referrer',
      capturedAt,
    }),
    tool: freeToolKind,
  };

  const value = encodeAttrCookie({ ...existing, jobId: freeToolJobId });
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${SIGNUP_ATTR_COOKIE}=${value}; Max-Age=${SIGNUP_ATTR_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure}`;
}

/** Same analytics as the gradient CTA; call for any entry into the transcribe handoff funnel. */
export function trackFreeToolTranscribeIntent(
  freeToolKind: TranscribeCtaToolKind,
  freeToolJobId: string
): void {
  // Stamp the jobId on the attribution cookie so the handoff page (after Clerk's OAuth dance)
  // can write users.signup_source with the exact tool + job that drove the signup.
  stampJobIdOnAttrCookie(freeToolKind, freeToolJobId);
  emitClientEvent('/api/events/conversion', {
    name: 'free_tool_transcribe_cta_clicked',
    free_tool_kind: freeToolKind,
    free_tool_job_id: freeToolJobId,
  });
  // Mirror to the GTM dataLayer so a Google Ads (or other) conversion tag can fire on this
  // lead-gen intent with the user's gclid. GTM maps this event to the conversion action in its UI.
  pushToDataLayer({
    event: 'free_tool_transcribe_cta_clicked',
    free_tool_kind: freeToolKind,
    free_tool_job_id: freeToolJobId,
  });
}

export interface FreeToolHandoffLinks {
  ready: boolean;
  /**
   * Primary CTA: direct handoff URL. Logged-out users are sent to /sign-in?redirect_url=... by middleware
   * (Clerk only wraps the app under /dashboard; public /tools pages must not use useAuth).
   */
  primaryHref: string;
  handoffPath: string | null;
}

/** Handoff URLs without Clerk — safe on public marketing layouts. */
export function useFreeToolTranscribeHandoffLinks(
  jobId: string | null,
  downloadToken: string | null
): FreeToolHandoffLinks {
  return useMemo(() => {
    const ready = Boolean(jobId && downloadToken);

    const handoffPath =
      ready && jobId && downloadToken ? buildTranscribeHandoffPath(jobId, downloadToken) : null;

    const primaryHref = handoffPath ?? '#';

    return { ready, primaryHref, handoffPath };
  }, [jobId, downloadToken]);
}

export interface FreeToolTranscribeCtaProps {
  jobId: string | null;
  downloadToken: string | null;
  freeToolKind: TranscribeCtaToolKind;
  className?: string;
  children?: React.ReactNode;
}

/**
 * Primary gradient CTA for free-tool → transcribe handoff.
 * Pair with {@link useFreeToolTranscribeHandoffLinks} for inline “Log in” links in copy.
 */
export function FreeToolTranscribeCta({
  jobId,
  downloadToken,
  freeToolKind,
  className,
  children = 'Transcribe in Wisprs, start for free',
}: FreeToolTranscribeCtaProps) {
  const { ready, primaryHref } = useFreeToolTranscribeHandoffLinks(jobId, downloadToken);

  function handlePrimaryClick(): void {
    if (jobId && downloadToken) {
      trackFreeToolTranscribeIntent(freeToolKind, jobId);
    }
  }

  return (
    <Link
      href={primaryHref}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handlePrimaryClick}
      className={cn(
        GRADIENT_CTA_CLASS,
        'w-full',
        !ready && 'pointer-events-none opacity-50',
        className
      )}
      aria-disabled={!ready}
    >
      {children}
    </Link>
  );
}
